/*
 * Crown & Paw — new cart drawer behaviour (nav/merch rebuild).
 * Workflow per developer-spec §8 + 03-cart-logic.js. Shopify automatic discounts are the
 * source of truth for prices; this only DISPLAYS estimated savings + nudges using the same rules.
 *
 * Integration: when the new drawer is present (section enabled), it overrides the theme's
 * open_cart_drawer()/reBuildCart() globals so add-to-cart (product.js) opens/refreshes THIS drawer,
 * and intercepts cart triggers. When the section is disabled it renders nothing and this is inert.
 */
(function () {
  'use strict';

  var SECTION_ID = 'cp-cart-drawer';
  var THRESHOLD_DEFAULT = 7500;

  // Offer rules (mirror 03-cart-logic.js CONFIG).
  var OFFERS = {
    'wall-art': { rate: 0.30, mode: 'pair', kicker: 'WALL ART OFFER', headline: 'Add 1 more portrait, get 30% off the cheaper one' },
    'clothing': { rate: 0.20, mode: 'pair', kicker: 'CLOTHING OFFER', headline: 'Add 1 more clothing item or accessory, get 20% off the cheaper one' },
    'other':    { rate: 0.10, mode: 'all', kicker: 'BUNDLE & SAVE',  headline: 'Add 1 more, save 10% on your order' }
  };
  var PRIORITY = ['wall-art', 'clothing', 'other'];
  var BW = { start: new Date('2026-11-23T00:00:00-05:00'), end: new Date('2026-11-30T23:59:59-05:00'), rate: 0.25 };
  var MAX_SUGG = 6;

  function root() { return document.querySelector('[data-cp-cart-drawer]'); }
  function isEmpty() { var r = root(); return !r || r.classList.contains('is-empty'); }

  /* ---- analytics (04-tracking-plan.csv) ----
     Vendor-neutral: push to window.dataLayer (GTM) AND dispatch a DOM event, so either a GTM
     container or a Shopify custom pixel can consume it. No-op until one is connected; needs no IDs.
     Never send PII (gift text / pet names) — only counts / booleans. GTM Consent Mode gates sending. */
  function track(name, params) {
    params = params || {};
    try {
      var o = { event: name };
      for (var k in params) { if (Object.prototype.hasOwnProperty.call(params, k)) o[k] = params[k]; }
      (window.dataLayer = window.dataLayer || []).push(o);
      document.dispatchEvent(new CustomEvent('cp:' + name, { detail: params }));
    } catch (e) {}
  }
  function cartValue() { var r = root(); return r ? (parseInt(r.dataset.cartTotal, 10) || 0) / 100 : 0; }
  function itemCount() { var el = qs('[data-cp-cart-count]'); return el ? (parseInt(el.textContent.replace(/[^\d]/g, ''), 10) || 0) : 0; }
  var lastNudgeView = null;
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  /* ---- money (Shopify.formatMoney, trimmed) ---- */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '', placeholderRegex = /\{\{\s*(\w+)\s*\}\}/, fmt = format || '${{amount}}';
    function defaultTo(n, d) { return (n === 0 || n) ? n : d; }
    function num(number, precision, thousands, decimal) {
      precision = defaultTo(precision, 2); thousands = defaultTo(thousands, ','); decimal = defaultTo(decimal, '.');
      if (isNaN(number) || number == null) return 0;
      number = (number / 100).toFixed(precision);
      var parts = number.split('.'),
        dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
        cents2 = parts[1] ? decimal + parts[1] : '';
      return dollars + cents2;
    }
    switch ((fmt.match(placeholderRegex) || [])[1]) {
      case 'amount': value = num(cents, 2); break;
      case 'amount_no_decimals': value = num(cents, 0); break;
      case 'amount_with_comma_separator': value = num(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = num(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = num(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = num(cents, 0, ' ', ''); break;
      default: value = num(cents, 2);
    }
    return fmt.replace(placeholderRegex, value);
  }
  function money(cents) { return formatMoney(Math.round(cents), (root() && root().dataset.moneyFormat) || '${{amount}}').replace(/<[^>]*>/g, ''); }

  /* ---- cart mutations ---- */
  function post(url, data) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(data)
    }).then(function (r) {
      // Reject on HTTP errors (e.g. 422 when a line is unavailable) so callers' .catch handlers run,
      // instead of resolving a failure as success and rebuilding/redirecting on an unchanged cart.
      return r.json().catch(function () { return null; }).then(function (body) {
        if (!r.ok) { var err = new Error('Cart request failed: ' + r.status); err.status = r.status; err.body = body; throw err; }
        return body;
      });
    });
  }
  var busy = false, loaderEl = null;
  var rebuildToken = 0; // bumped per rebuild() so stale concurrent responses can't swap in
  function setLoading(on) {
    if (!loaderEl) {
      loaderEl = document.createElement('div');
      loaderEl.className = 'cp-cart__loading';
      loaderEl.setAttribute('aria-hidden', 'true');
      loaderEl.innerHTML = '<span class="cp-cart__spinner"></span>';
      document.body.appendChild(loaderEl); // outside .cp-cart so rebuild() innerHTML swap can't remove it
    }
    loaderEl.classList.toggle('is-on', !!on);
  }
  function withBusy(p) {
    busy = true; setLoading(true);
    function done() { busy = false; setLoading(false); }
    // Persist any pending gift-message edit BEFORE the rebuild re-renders the textarea from the server,
    // otherwise a rebuild within the 600ms debounce window restores the old text and the edit is lost.
    return p.then(function (v) { return flushGiftMsg().then(function () { return v; }); })
      .then(function (v) { return rebuild().then(function () { done(); return v; }); })
      .catch(function (e) { done(); console.warn('[cp-cart]', e); });
  }

  /* ---- refresh via Section Rendering ---- */
  function rebuild() {
    // Supersede token: the add path fires 3 rebuilds at once and a qty change fires its own.
    // Only the latest-issued rebuild may swap the DOM, so a slow stale response can't clobber fresh totals.
    var myToken = ++rebuildToken;
    return fetch('/?section_id=' + SECTION_ID, { headers: { 'Accept': 'text/html' } })
      .then(function (r) {
        // Reject HTTP errors so withBusy's catch can recover/surface instead of silently leaving a stale drawer.
        if (!r.ok) throw new Error('cart rebuild failed: ' + r.status);
        return r.text();
      })
      .then(function (html) {
        if (myToken !== rebuildToken) return; // a newer rebuild superseded this one — discard stale markup
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var fresh = tmp.querySelector('[data-cp-cart-drawer]');
        var cur = root();
        // Missing drawer markup = a bad/partial response; reject rather than leave stale quantities/keys.
        if (!fresh || !cur) throw new Error('cart rebuild: drawer markup missing');
        var wasOpen = cur.classList.contains('is-open');
        cur.innerHTML = fresh.innerHTML;
        Array.prototype.forEach.call(fresh.attributes, function (a) {
          if (a.name !== 'class' && a.name !== 'hidden') cur.setAttribute(a.name, a.value);
        });
        // refresh suggestions json
        var freshSugg = tmp.querySelector('[data-cp-cart-suggestions]');
        var curSugg = qs('[data-cp-cart-suggestions]');
        if (freshSugg && curSugg) curSugg.textContent = freshSugg.textContent;
        if (wasOpen) cur.classList.add('is-open');
        compute();
        updateCartIcon();
      });
  }

  /* ---- read helpers ---- */
  function lines() {
    return qsa('[data-cp-cart-line]').map(function (el) {
      return { og: (el.dataset.og || 'other'), linked: el.dataset.linked === 'true', price: parseInt(el.dataset.price, 10) || 0, qty: parseInt(el.dataset.qty, 10) || 0, handle: el.dataset.handle || '', variant: el.dataset.variant || '', el: el };
    });
  }
  function suggestions() {
    var s = qs('[data-cp-cart-suggestions]');
    if (!s) return {};
    try { return JSON.parse(s.textContent) || {}; } catch (e) { return {}; }
  }
  // Parse a store-timezone epoch (seconds, emitted server-side via | date: '%s') into a Date, so the
  // window is the same wall-clock moment for every shopper regardless of their own timezone.
  function parseBW(s) {
    if (!s) return null;
    var n = parseInt(String(s).trim(), 10);
    return isNaN(n) ? null : new Date(n * 1000);
  }
  function isBlackWeek() {
    var r = root();
    var start = (r && parseBW(r.dataset.bwStart)) || BW.start;
    var end = (r && parseBW(r.dataset.bwEnd)) || BW.end;
    var n = new Date();
    return n >= start && n <= end;
  }
  // Re-run compute() at the next Black Week boundary (start/end), so a drawer left open across the
  // boundary flips the nudge on/off instead of waiting for another cart action or reopen.
  var bwTimer = null;
  function scheduleBwRefresh() {
    if (bwTimer) { clearTimeout(bwTimer); bwTimer = null; }
    var r = root();
    var start = (r && parseBW(r.dataset.bwStart)) || BW.start;
    var end = (r && parseBW(r.dataset.bwEnd)) || BW.end;
    var now = Date.now();
    var next = 0;
    if (start && start.getTime() > now) next = start.getTime();
    if (end && end.getTime() > now && (next === 0 || end.getTime() < next)) next = end.getTime();
    if (!next) return;
    var delay = next - now;
    if (delay > 2147483647) return;   // beyond setTimeout's range
    bwTimer = setTimeout(function () { bwTimer = null; compute(); }, delay);
  }

  /* Keep the theme's header cart-icon badge in sync (new cp-header + old header). */
  function updateCartIcon() {
    var countEl = qs('[data-cp-cart-count]');
    var count = countEl ? (parseInt(countEl.textContent, 10) || 0) : 0;
    qsa('[data-cart-count]').forEach(function (el) {
      el.textContent = count;
      if (count > 0) el.removeAttribute('hidden'); else el.setAttribute('hidden', '');
    });
    qsa('.cart_trigger').forEach(function (el) {
      el.setAttribute('cart_item_count', count);
      el.classList.toggle('show_bubble', count > 0);
    });
  }

  /* ---- compute ----
     Summary, bar, saving line, discount rows and totals are now SERVER-RENDERED from the
     real Shopify cart (so the drawer always matches checkout). JS only drives the nudge
     projection (a "what if you add one more" hint), the Black-Week banner, the gift-card
     visibility, and the empty-state class. */
  function compute() {
    var r = root(); if (!r) return;
    var threshold = parseInt(r.dataset.threshold, 10) || THRESHOLD_DEFAULT;
    var merch = parseInt(r.dataset.merch, 10) || 0;          // real merch total (after discounts)
    var qualifying = parseInt(r.dataset.qualifying, 10);     // merch + paid add-ons (free-shipping basis)
    if (isNaN(qualifying)) qualifying = merch;
    var freeShip = r.dataset.freeShip === 'true';
    var mainCount = parseInt(r.dataset.mainCount, 10) || 0;
    var ls = lines();
    var bw = isBlackWeek();

    // units by group — used ONLY for the nudge projection. Built from main merchandise only: a linked
    // add-on (sleeve, storybook, etc.) isn't a promotional unit, so counting it would skew the offer.
    var units = { 'wall-art': [], 'clothing': [], 'other': [] };
    ls.forEach(function (l) { if (l.linked) return; var g = units[l.og] ? l.og : 'other'; for (var i = 0; i < l.qty; i++) units[g].push(l.price); });
    Object.keys(units).forEach(function (g) { units[g].sort(function (a, b) { return a - b; }); });

    r.classList.toggle('is-empty', mainCount === 0);
    toggle(qs('[data-cp-cart-bw]'), bw);
    // Gift card eligibility (poster/wall-art or canvas in cart) is decided server-side and exposed as
    // data-gift-eligible; also surface it when a gift product is already in the cart so it stays removable.
    toggle(qs('[data-cp-cart-gift]'), r.dataset.giftEligible === 'true' || r.dataset.giftOn === 'true');

    buildNudge(bw, units, ls, qualifying, freeShip, threshold);
    syncCountdown();
    scheduleBwRefresh();   // re-arm the Black Week boundary timer so an open drawer flips at start/end
  }

  // Spec §2.2: while the drawer is open, the countdown shows the earliest cutoff among the
  // cart's product types; closed → restore the page's own countdown.
  function syncCountdown() {
    if (!window.cpCountdown) return;
    var r = root();
    if (r && r.classList.contains('is-open')) {
      var seen = {}, list = [];
      qsa('[data-cp-cart-line]').forEach(function (el) {
        var c = (el.dataset.cutoff || '').trim();
        if (c && !seen[c]) { seen[c] = 1; list.push(c); }
      });
      // Pass even an empty list so applyCart can pick a global ('all') entry, or hide an unrelated page countdown.
      window.cpCountdown.applyCart(list);
    } else {
      window.cpCountdown.clearCart();
    }
  }
  function setText(sel, txt) { var el = qs(sel); if (el) el.textContent = txt; }
  function toggle(el, show) { if (el) { if (show) el.removeAttribute('hidden'); else el.setAttribute('hidden', ''); } }

  /* ---- nudge (one max; priority wall-art → clothing → other; BW overrides) ---- */
  function buildNudge(bw, units, ls, qualifying, freeShip, threshold) {
    var box = qs('[data-cp-cart-nudge]'); if (!box) return;
    var pools = suggestions();
    var inCart = {}; ls.forEach(function (l) { inCart[l.handle] = true; });
    function pick(group) { return (pools[group] || []).filter(function (p) { return p.available && !inCart[p.handle]; }).slice(0, MAX_SUGG); }

    var nudge = null;
    if (bw) {
      var sg = pick('small-gifts');
      if (sg.length) {
        var r = root();
        nudge = {
          group: 'black-week',
          kicker: (r && r.dataset.bwKicker) || 'BLACK WEEK',
          headline: (r && r.dataset.bwHeadline) || 'Add a little extra — still 25% off',
          sub: (r && r.dataset.bwSub) || 'Small gifts and stocking fillers',
          sugg: sg
        };
      }
    } else {
      var group = PRIORITY.filter(function (g) { return units[g].length === 1; })[0];
      if (group) {
        var offer = OFFERS[group];
        var sugg = pick(group);
        if (sugg.length) {
          var current = units[group][0];
          var cheapest = Math.min.apply(null, sugg.map(function (p) { return p.price; }));
          var save = offer.mode === 'all' ? (current + cheapest) * offer.rate : Math.min(current, cheapest) * offer.rate;
          var unlocks = !freeShip && (qualifying + cheapest - save) >= threshold;
          nudge = { group: group, kicker: offer.kicker, headline: offer.headline, sub: 'You’d save ' + money(save) + (unlocks ? ' and unlock free shipping' : ''), sugg: sugg };
        }
      }
    }

    if (!nudge) { box.setAttribute('hidden', ''); lastNudgeView = null; return; }
    box.removeAttribute('hidden');
    // cart_nudge_view — fire once per distinct nudge (not on every rebuild/qty change)
    if (lastNudgeView !== nudge.group) {
      track('cart_nudge_view', { nudge_group: nudge.group, suggestion_count: nudge.sugg.length });
      lastNudgeView = nudge.group;
    }
    setText('[data-cp-cart-nudge-kicker]', nudge.kicker);
    setText('[data-cp-cart-nudge-headline]', nudge.headline);
    setText('[data-cp-cart-nudge-sub]', nudge.sub);
    var scroll = qs('[data-cp-cart-sugg]'); if (!scroll) return;
    scroll.innerHTML = '';
    nudge.sugg.forEach(function (p, i) {
      var card = document.createElement('div');
      card.className = 'cp-cart__sugg-card';
      var onSale = p.compareAt && p.compareAt > p.price;
      var priceHtml = p.priceLabel + (onSale ? ' <s class="cp-cart__sugg-was">' + money(p.compareAt) + '</s>' : '');
      var saveHtml = onSale ? '<span class="cp-cart__sugg-save">Save ' + money(p.compareAt - p.price) + '</span>' : '';
      card.innerHTML =
        '<div class="cp-cart__sugg-img"' + (p.img ? ' style="background-image:url(\'' + p.img + '\')"' : '') + '></div>' +
        '<span class="cp-cart__sugg-name"></span>' +
        '<span class="cp-cart__sugg-price">' + priceHtml + '</span>' +
        saveHtml +
        '<button type="button" class="cp-cart__sugg-add">+ Add</button>';
      card.querySelector('.cp-cart__sugg-name').textContent = p.title;
      card.querySelector('.cp-cart__sugg-add').addEventListener('click', function () {
        track('cart_nudge_add_click', { nudge_group: nudge.group, product_id: p.productId, position: i + 1 });
        openAdd(p);
      });
      scroll.appendChild(card);
    });
  }

  /* ---- +Add personalisation overlay ---- */
  // "+ Add" upsell: add now, photo collected later via emailed link (spec §8.3 upload-later path).
  var pending = null, formState = { name: '' }, addOpener = null;
  function openAdd(p) {
    pending = p; formState = { name: '' };
    var ov = qs('[data-cp-cart-add]'); if (!ov) return;
    addOpener = document.activeElement;   // the "+ Add" button to restore focus to on close
    var img = qs('[data-cp-cart-add-img]');
    if (img) img.style.backgroundImage = p.img ? "url('" + p.img + "')" : '';
    setText('[data-cp-cart-add-name]', p.title);
    setText('[data-cp-cart-add-sub]', p.priceLabel);
    var nameInput = qs('[data-cp-cart-add-name-input]'); if (nameInput) nameInput.value = '';
    var confirm = qs('[data-cp-cart-add-confirm]'); if (confirm) confirm.classList.add('is-ready');
    ov.removeAttribute('hidden');
    // Move focus into the overlay so Tab stays in it (the trap scopes to the overlay while it's open).
    var ft = nameInput || ov.querySelector('a[href], button:not([disabled]), input, [tabindex]');
    if (ft) { try { ft.focus(); } catch (e) {} }
  }
  function closeAdd() {
    pending = null;
    var ov = qs('[data-cp-cart-add]'); if (ov) ov.setAttribute('hidden', '');
    if (addOpener) { try { addOpener.focus(); } catch (e) {} addOpener = null; }
  }
  function confirmAdd() {
    if (!pending) return;
    var variantId = parseInt(pending.variant, 10) || pending.id; // capture BEFORE closeAdd() nulls pending
    var nameGiven = formState.name.trim() ? true : false;
    var props = { 'Photo': 'later', '__photo_later': 'true' };
    // Stamp a configurator-style linkage key so later add-ons (dd/sq/gift) can link to this artwork via
    // __linked_time and removeMainLine removes them together — else the service orphans in a multi-product cart.
    props['__time'] = String(Date.now() * 1000 + Math.floor(Math.random() * 1000));
    if (nameGiven) props['Pet Name'] = formState.name.trim();
    // photo_status is always 'later' — the drawer only offers the upload-later path (spec §8.3 deviation)
    track('cart_personalise_complete', { product_id: pending.productId, photo_status: 'later', pet_name_given: nameGiven });
    closeAdd();
    withBusy(post('/cart/add.js', { items: [{ id: variantId, quantity: 1, properties: props }] }));
  }

  /* ---- add-on + gift toggles ---- */
  function toggleAddon(kind) {
    var r = root(); if (!r) return;
    var variant = kind === 'dd' ? r.dataset.ddVariant : r.dataset.sqVariant;
    if (!variant) return;
    var on = (kind === 'dd' ? r.dataset.ddOn : r.dataset.sqOn) === 'true';
    track('cart_addon_toggle', { addon: kind === 'dd' ? 'digital-download' : 'skip-the-queue', state: on ? 'off' : 'on' });
    if (on) {
      // Remove EVERY line with this add-on variant by its line key — several products can hold a
      // linked add-on of the same configured variant, so change.js by variant id would miss duplicates.
      withBusy(fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          var ops = Promise.resolve();
          (cart.items || []).forEach(function (it) {
            if (String(it.variant_id) === String(variant)) {
              ops = ops.then(function () { return post('/cart/change.js', { id: it.key, quantity: 0 }); });
            }
          });
          return ops;
        }));
    } else {
      // Link the service to the artwork parent so removeMainLine removes it when that product goes
      // (otherwise it can reach checkout with no artwork to process).
      var addonProps = {};
      if (r.dataset.artworkParentTime) addonProps['__linked_time'] = r.dataset.artworkParentTime;
      var addItem = { id: parseInt(variant, 10), quantity: 1 };
      if (addonProps['__linked_time']) addItem.properties = addonProps;
      withBusy(post('/cart/add.js', { items: [addItem] }));
    }
  }
  // Write the gift message onto the real line-item properties the configurator uses (parent
  // '_Gift Message' + add-on 'Gift Message') — what fulfillment reads, not just the cart attribute.
  // Empty text clears them. Merges each line's existing properties so photo/pet-name/etc. are kept.
  function syncGiftProps(text) {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items = cart.items || [];
        // Count distinct configurator gift groups (linked parent/add-on pairs, keyed by __linked_time/__time).
        // The drawer has a single message field, so with more than one group we must NOT broadcast — that would
        // overwrite each item's own note. In that case leave the linked lines untouched (preserve their messages).
        var links = {};
        // Parent __time values that a gift add-on links to (drawer-added gifts carry __linked_time
        // but the parent line has no _Gift Message yet), so we can write the message onto the parent too.
        var giftLinkTimes = {};
        items.forEach(function (it) {
          var p = it.properties || {};
          var link = p['__linked_time'] || p['__time'];
          var isGiftAddon = p['__gift_message_addon'] === 'true';
          var hasMsg = Object.prototype.hasOwnProperty.call(p, '_Gift Message') || Object.prototype.hasOwnProperty.call(p, 'Gift Message');
          if (link && (hasMsg || isGiftAddon)) links[String(link)] = true;
          if (isGiftAddon && p['__linked_time'] != null) giftLinkTimes[String(p['__linked_time'])] = true;
        });
        var multiGroups = Object.keys(links).length > 1;
        // The group the single textarea currently displays — in a multi-gift cart we write the edit
        // to this group only (not broadcast, not silently drop it), so line props match what's shown.
        var ta = qs('[data-cp-cart-gift-msg]');
        var activeGroup = ta ? String(ta.getAttribute('data-cp-cart-gift-group') || '') : '';
        var ops = Promise.resolve();
        items.forEach(function (it) {
          var p = it.properties || {};
          var isAddon = p['__gift_message_addon'] === 'true';
          var hasParent = Object.prototype.hasOwnProperty.call(p, '_Gift Message');
          var hasVisible = Object.prototype.hasOwnProperty.call(p, 'Gift Message');
          // The configurator parent a drawer-added gift is linked to — needs _Gift Message written even
          // though it doesn't carry one yet (fulfillment reads line-item properties as source of truth).
          var isLinkedParent = p['__time'] != null && giftLinkTimes[String(p['__time'])] === true;
          if (!isAddon && !hasParent && !hasVisible && !isLinkedParent) return;
          // Preserve distinct per-item messages only when WRITING a message (edit); removal (empty text)
          // must clear every group's parent, or a "removed" message stays attached and re-enables the toggle.
          var linked = !!(p['__linked_time'] || p['__time']);
          if (text && multiGroups && linked) {
            // Write to the displayed group only; skip other groups (and, if the active group is unknown,
            // skip all — the original safe behaviour — rather than mis-target an edit).
            var myGroup = String(p['__linked_time'] || p['__time'] || '');
            if (!activeGroup || myGroup !== activeGroup) return;
          }
          var np = {};
          for (var k in p) { if (Object.prototype.hasOwnProperty.call(p, k)) np[k] = p[k]; }
          if (isAddon || hasVisible) { if (text) { np['Gift Message'] = text; } else { delete np['Gift Message']; } }
          if (hasParent || isLinkedParent) { if (text) { np['_Gift Message'] = text; } else { delete np['_Gift Message']; } }
          ops = ops.then(function () { return post('/cart/change.js', { id: it.key, quantity: it.quantity, properties: np }); });
        });
        return ops;
      });
  }
  // Flush the current textarea message onto the line-item properties (used at checkout).
  function flushGiftProps() {
    var r = root();
    if (!r || r.dataset.giftOn !== 'true') return Promise.resolve();
    var ta = qs('[data-cp-cart-gift-msg]');
    return syncGiftProps(ta ? ta.value.slice(0, 200) : '');
  }
  function toggleGift() {
    var r = root(); if (!r) return;
    var on = r.dataset.giftOn === 'true';
    var variant = r.dataset.giftVariant;
    track('cart_addon_toggle', { addon: 'gift-message', state: on ? 'off' : 'on' });
    if (on) {
      // Cancel any pending debounced message save, or it fires after removal and restores the old attribute.
      clearTimeout(msgTimer); msgTimer = null;   // null it so flushGiftMsg() in withBusy sees no pending save
      // Remove EVERY gift add-on line by its line key — several products can share one gift variant, so
      // removing by variant id would leave duplicates behind. Match the marker or the gift variant ids.
      var giftVars = {};
      (r.dataset.giftRemove || variant || '').split(',').filter(Boolean).forEach(function (v) { giftVars[String(v)] = true; });
      withBusy(fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          var ops = Promise.resolve();
          (cart.items || []).forEach(function (it) {
            var p = it.properties || {};
            if (p['__gift_message_addon'] === 'true' || giftVars[String(it.variant_id)]) {
              ops = ops.then(function () { return post('/cart/change.js', { id: it.key, quantity: 0 }); });
            }
          });
          // Also clear the message from the parent line properties, or the rebuild re-detects it and flips the toggle back on.
          return ops
            .then(function () { return syncGiftProps(''); })
            .then(function () { return post('/cart/update.js', { attributes: { 'Gift Message': '' } }); });
        }));
    } else {
      // open body immediately for UX; add product tagged (so edit/remove find it) and linked to the
      // wall-art/canvas parent (so removeMainLine removes it when that product goes).
      var body = qs('[data-cp-cart-gift-body]'); if (body) body.removeAttribute('hidden');
      var giftProps = { '__gift_message_addon': 'true' };
      if (r.dataset.giftParentTime) giftProps['__linked_time'] = r.dataset.giftParentTime;
      var addP = variant ? post('/cart/add.js', { items: [{ id: parseInt(variant, 10), quantity: 1, properties: giftProps }] }) : Promise.resolve();
      withBusy(addP);
    }
  }
  var msgTimer = null;
  function onGiftMsg(val) {
    var trimmed = val.slice(0, 200);
    setText('[data-cp-cart-gift-count]', trimmed.length + ' / 200');
    var prev = qs('[data-cp-cart-gift-preview]');
    if (prev) { prev.textContent = trimmed.trim() ? trimmed : 'Your message will appear here…'; prev.classList.toggle('is-placeholder', !trimmed.trim()); }
    if (trimmed.trim()) clearGiftError();   // user is typing a message → clear the "required" state
    clearTimeout(msgTimer);
    // Save via flushGiftMsg so the debounced write also persists to the targeted group's line props
    // (not just the cart attribute) — else a reload/navigate with no further cart action loses the group.
    msgTimer = setTimeout(function () { flushGiftMsg(); }, 600);
  }
  // Persist a pending debounced gift message immediately (called before a rebuild) so the saved attribute
  // matches the textarea. No-op when nothing is pending — e.g. a removal already cleared the timer.
  function flushGiftMsg() {
    if (!msgTimer) return Promise.resolve();
    clearTimeout(msgTimer); msgTimer = null;
    var ta = qs('[data-cp-cart-gift-msg]');
    if (!ta) return Promise.resolve();
    var text = ta.value.slice(0, 200);
    // Write the cart attribute AND persist the edit onto the targeted group's line props, so the rebuild
    // can still match the attribute to its group. Attribute-only would drop the association, clearing
    // data-cp-cart-gift-group and locking the field in a multi-gift cart (edit then skipped at checkout).
    return post('/cart/update.js', { attributes: { 'Gift Message': text } })
      .then(function () { return syncGiftProps(text); });
  }

  // Gift message is mandatory once the gift card add-on is ticked (spec §8.4).
  function giftMessageMissing() {
    var r = root();
    if (!r || r.dataset.giftOn !== 'true') return false;   // gift card not added → not required
    var ta = qs('[data-cp-cart-gift-msg]');
    if (!ta) return true;
    // Read-only multi-gift field: the drawer can't target one message per group, so it's set per item
    // on the PDP — don't block checkout on this blank field (it can't be edited here).
    if (ta.readOnly) return false;
    return ta.value.trim().length === 0;
  }
  function flagGiftMissing() {
    var body = qs('[data-cp-cart-gift-body]'); if (body) body.removeAttribute('hidden');
    var ta = qs('[data-cp-cart-gift-msg]');
    var err = qs('[data-cp-cart-gift-err]');
    if (err) err.removeAttribute('hidden');
    if (ta) { ta.classList.add('is-error'); try { ta.focus(); } catch (e) {} ta.scrollIntoView({ block: 'center', behavior: 'smooth' }); }
  }
  function clearGiftError() {
    var ta = qs('[data-cp-cart-gift-msg]'); if (ta) ta.classList.remove('is-error');
    var err = qs('[data-cp-cart-gift-err]'); if (err) err.setAttribute('hidden', '');
  }
  // Checkout attrs: Checkout+ choice + latest gift message (flushes the debounced save so the redirect can't drop it).
  function checkoutAttrs(checkoutPlus) {
    clearTimeout(msgTimer); msgTimer = null;
    var attrs = { 'Checkout+': checkoutPlus };
    var r = root();
    if (r && r.dataset.giftOn === 'true') {
      var ta = qs('[data-cp-cart-gift-msg]');
      if (ta) attrs['Gift Message'] = ta.value.slice(0, 200);
    }
    return attrs;
  }

  /* ---- qty / remove ---- */
  function changeLine(key, qty) { withBusy(post('/cart/change.js', { id: key, quantity: qty })); }
  // Remove a main product AND everything the configurator links to it in one atomic update, else they orphan:
  //  - every add-on linked via __linked_time == the parent's __time (gift message, fast track, rush,
  //    sleeve, ebook, generic __addon, …) — match the link, not individual flags
  //  - the free mug, which clones the parent's __time onto its own __time and carries __free-mug.
  function removeMainLine(key) {
    withBusy(fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items = cart.items || [];
        var parent = null;
        for (var i = 0; i < items.length; i++) { if (items[i].key === key) { parent = items[i]; break; } }
        var updates = {}; updates[key] = 0;
        var removedGift = false;
        var linkTime = parent && parent.properties ? parent.properties['__time'] : null;
        if (linkTime != null) {
          linkTime = String(linkTime);
          items.forEach(function (it) {
            if (it.key === key) return;
            var p = it.properties || {};
            var lt = (p['__linked_time'] != null) ? String(p['__linked_time']) : '';
            var t = (p['__time'] != null) ? String(p['__time']) : '';
            var isLinkedAddon = lt && lt === linkTime;   // any add-on linked to this parent, whatever its flag
            var isFreeMug = t && t === linkTime && p['__free-mug'] === 'true';
            if (isLinkedAddon || isFreeMug) {
              updates[it.key] = 0;
              if (p['__gift_message_addon'] === 'true') removedGift = true;
            }
          });
        }
        var payload = { updates: updates };
        // If this removal deletes a linked gift add-on and no other gift line remains, clear the
        // cart-level Gift Message attribute too — else rebuild keeps data-gift-on=true and, with no
        // textarea on a non-eligible remaining item, giftMessageMissing() blocks checkout forever.
        if (removedGift) {
          var giftRemains = items.some(function (it) {
            return updates[it.key] !== 0 && it.properties && it.properties['__gift_message_addon'] === 'true';
          });
          // The cart-level Gift Message attribute represents the ACTIVE (displayed) group. Clear it when no
          // gift remains OR when the removed group IS the active one — otherwise its stale text would be
          // written onto the surviving group at checkout.
          var ta = qs('[data-cp-cart-gift-msg]');
          var activeGroup = ta ? String(ta.getAttribute('data-cp-cart-gift-group') || '') : '';
          var removedActive = activeGroup && activeGroup === linkTime;
          if (!giftRemains || removedActive) {
            payload.attributes = { 'Gift Message': '' };
            // Discard the pending debounced save so withBusy's flush can't re-add the removed group's text.
            clearTimeout(msgTimer); msgTimer = null;
          }
        }
        return post('/cart/update.js', payload);
      }));
  }
  // Change a main product's quantity AND keep its quantity-scaled add-ons (the sleeve, charged per unit)
  // in sync — matched by __linked_time == the parent's __time — in one atomic update.
  function changeMainQty(key, qty) {
    withBusy(fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items = cart.items || [];
        var parent = null;
        for (var i = 0; i < items.length; i++) { if (items[i].key === key) { parent = items[i]; break; } }
        var updates = {}; updates[key] = qty;
        var linkTime = parent && parent.properties ? parent.properties['__time'] : null;
        if (linkTime != null) {
          linkTime = String(linkTime);
          items.forEach(function (it) {
            if (it.key === key) return;
            var p = it.properties || {};
            var lt = (p['__linked_time'] != null) ? String(p['__linked_time']) : '';
            // Quantity-scaled add-ons (currently the sleeve) track the parent's quantity; per-order ones stay as-is.
            if (lt && lt === linkTime && p['__sleeve_addon'] === 'true') { updates[it.key] = qty; }
          });
        }
        return post('/cart/update.js', { updates: updates });
      }));
  }
  // Removing the LAST main product clears the whole cart, so orphan add-ons/gift don't linger
  // (otherwise the drawer looks empty but the cart icon still shows the add-on count).
  function changeMainLine(key, qty) {
    var r = root();
    var mainCount = r ? (parseInt(r.dataset.mainCount, 10) || 0) : 0;
    if (qty <= 0) {
      if (mainCount <= 1) {
        // Whole cart is cleared — discard any pending message save so it can't re-write the Gift
        // Message attribute afterward and re-enable the gift on the next rebuild.
        clearTimeout(msgTimer); msgTimer = null;
        withBusy(post('/cart/clear.js', {}));
      } else {
        // removeMainLine discards the pending save only if this removal actually takes the gift with it;
        // on an unrelated removal the gift parent remains, so withBusy flushes and preserves the edit.
        removeMainLine(key);
      }
    } else {
      changeMainQty(key, qty);
    }
  }

  /* ---- open / close ---- */
  var lastFocus = null;
  // Visible focusable elements inside a container (in DOM order).
  function focusablesIn(el) {
    var nodes = el.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])');
    var out = [];
    for (var i = 0; i < nodes.length; i++) { if (nodes[i].offsetParent !== null || nodes[i] === document.activeElement) out.push(nodes[i]); }
    return out;
  }
  function openDrawer() {
    var r = root(); if (!r) return;
    var ae = document.activeElement;
    if (ae && !r.contains(ae)) lastFocus = ae;   // remember the opener to restore on close
    r.removeAttribute('hidden');
    void r.offsetWidth; // reflow so the slide-in transition runs
    r.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';
    compute();
    // Move focus into the dialog so Tab can't reach controls behind the overlay.
    var panel = r.querySelector('.cp-cart__panel');
    var ft = (panel && panel.querySelector('.cp-cart__close')) || (panel && focusablesIn(panel)[0]);
    if (ft) { try { ft.focus(); } catch (e) {} }
  }
  function closeDrawer() {
    var r = root(); if (!r) return;
    // Reset the +Add overlay on any drawer-close path, so reopening never resurfaces a stale personalise screen.
    var addOv = r.querySelector('[data-cp-cart-add]:not([hidden])');
    if (addOv) { addOv.setAttribute('hidden', ''); pending = null; addOpener = null; }
    r.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    if (window.cpCountdown) window.cpCountdown.clearCart();
    setTimeout(function () { if (r && !r.classList.contains('is-open')) r.setAttribute('hidden', ''); }, 320);
    if (lastFocus) { try { lastFocus.focus(); } catch (e) {} lastFocus = null; }
  }
  function openAndRefresh() {
    setLoading(true);
    return rebuild().then(function () { openDrawer(); setLoading(false); clearOrphansIfEmpty(); }).catch(function () { setLoading(false); openDrawer(); });
  }
  // Recovery: if the cart has no main product (is-empty) but lines still linger — orphan add-ons / free
  // mug / gift left when a parent was removed from the cart PAGE — clear them so the cart truly empties
  // instead of stranding an unremovable residual item behind the empty state.
  function clearOrphansIfEmpty() {
    var r = root(); if (!r) return;
    if ((parseInt(r.dataset.mainCount, 10) || 0) > 0) return;
    // Use the real cart item count, not rendered [data-cp-cart-line] nodes: configured dd/sq/gift add-ons
    // aren't rendered as lines and free gifts render as .cp-cart__mug, so counting nodes misses them.
    var cntEl = qs('[data-cp-cart-count]');
    var itemCount = cntEl ? (parseInt(cntEl.textContent, 10) || 0) : 0;
    if (itemCount === 0) return;   // already truly empty
    withBusy(post('/cart/clear.js', {}));
  }

  /* ---- events (delegated) ---- */
  document.addEventListener('click', function (e) {
    var r = root(); if (!r) return;

    // open triggers (cart icon / links) — only when enabled
    var trigger = e.target.closest('[data-cp-cart-open], a[href="/cart"], a[href$="/cart"], .cart-link, .js-drawer-open-cart, .slider_cart_trigger, [data-cart-toggle]');
    if (trigger && !e.target.closest('[data-cp-cart-drawer]')) {
      e.preventDefault();
      openAndRefresh();
      return;
    }

    if (!e.target.closest('[data-cp-cart-drawer]')) return;

    if (e.target.closest('[data-cp-cart-close]')) { closeDrawer(); return; }
    if (busy) return;

    var bump = e.target.closest('[data-cp-cart-bump]');
    if (bump) { toggleAddon(bump.getAttribute('data-cp-cart-bump')); return; }
    if (e.target.closest('[data-cp-cart-gift-toggle]')) { toggleGift(); return; }

    var qbtn = e.target.closest('[data-cp-cart-qty]');
    if (qbtn) {
      var line = qbtn.closest('[data-cp-cart-line]');
      var key = line.dataset.key, q = parseInt(line.dataset.qty, 10) || 1;
      changeMainLine(key, qbtn.getAttribute('data-cp-cart-qty') === 'inc' ? q + 1 : q - 1);
      return;
    }
    if (e.target.closest('[data-cp-cart-remove]')) {
      var l2 = e.target.closest('[data-cp-cart-line]');
      changeMainLine(l2.dataset.key, 0); return;
    }

    // +Add overlay controls
    if (e.target.closest('[data-cp-cart-add-cancel]')) { closeAdd(); return; }
    if (e.target.closest('[data-cp-cart-add-confirm]')) { confirmAdd(); return; }

    // checkout — inert while the cart is empty; blocked until the gift message is filled (if ticked)
    if (e.target.closest('[data-cp-cart-checkout]')) {
      if (isEmpty()) return;
      if (giftMessageMissing()) { flagGiftMissing(); return; }
      track('checkout_click', { type: 'checkout_plus', cart_value: cartValue(), item_count: itemCount() });
      setLoading(true);
      post('/cart/update.js', { attributes: checkoutAttrs('Yes') })
        .then(function () { return flushGiftProps(); })
        .then(function () { window.location.href = '/checkout'; })
        .catch(function (err) { setLoading(false); console.warn('[cp-cart] checkout', err); });
      return;
    }
    if (e.target.closest('[data-cp-cart-checkout-plain]')) {
      if (isEmpty()) return;
      if (giftMessageMissing()) { flagGiftMissing(); return; }
      track('checkout_click', { type: 'without_protection', cart_value: cartValue(), item_count: itemCount() });
      setLoading(true);
      post('/cart/update.js', { attributes: checkoutAttrs('') })
        .then(function () { return flushGiftProps(); })
        .then(function () { window.location.href = '/checkout'; })
        .catch(function (err) { setLoading(false); console.warn('[cp-cart] checkout', err); });
      return;
    }
  });

  document.addEventListener('input', function (e) {
    if (e.target.matches('[data-cp-cart-gift-msg]')) onGiftMsg(e.target.value);
    if (e.target.matches('[data-cp-cart-add-name-input]')) formState.name = e.target.value;
  });

  // gift_message_entered — fire when the textarea is left (blur) with text; count only, no text
  document.addEventListener('focusout', function (e) {
    if (e.target.matches('[data-cp-cart-gift-msg]')) {
      var len = e.target.value.trim().length;
      if (len > 0) track('gift_message_entered', { character_count: len });
    }
  });

  document.addEventListener('keydown', function (e) {
    var r = root(); if (!r || !r.classList.contains('is-open')) return;
    if (e.key === 'Escape') {
      // If the +Add overlay is open, Escape dismisses it first (not the whole drawer), or the
      // overlay stays unhidden with stale `pending` and reopening lands on the old personalise screen.
      if (r.querySelector('[data-cp-cart-add]:not([hidden])')) { closeAdd(); return; }
      closeDrawer(); return;
    }
    if (e.key === 'Tab') {
      // Trap within the +Add overlay while it's open; otherwise the whole cart panel.
      var scope = r.querySelector('[data-cp-cart-add]:not([hidden])') || r.querySelector('.cp-cart__panel') || r;
      var f = focusablesIn(scope);
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1], active = document.activeElement;
      if (!scope.contains(active)) { e.preventDefault(); first.focus(); return; }
      if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
    }
  });

  /* ---- hook into theme add-to-cart + expose globals ---- */
  function init() {
    if (!root()) return; // section disabled → inert
    window.open_cart_drawer = function () { openAndRefresh(); };   // refresh first so the just-added item shows
    window.reBuildCart = function () { return rebuild(); };
    document.addEventListener('cartUpdated', function () { openAndRefresh(); });
    compute();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
